/**
 * Recent sessions + drill-in timeline.
 *
 * Owns its own filters, sort, paging and queries so both hosts — the standalone
 * Session Traces page and the User Detail → Sessions tab — stay thin.
 *
 * Pass `userId` to scope to one person (the tab); omit it for the cross-user
 * list, which then offers a user picker inside the filter bar.
 *
 * See docs/FeatureDesigns/user-actions.md.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SearchFilterBar,
  DataTable,
  type FilterDef,
  type ColumnDef,
  type SortState,
} from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { SessionTimeline } from './SessionTimeline';
import type { ListSessionsResponse, SessionSummary } from './types';
import { formatDuration, formatStarted } from './format';
import './UserActions.css';

const PAGE_SIZE = 25;

/** How far back the cross-user list looks when nothing is chosen. */
const DEFAULT_DAYS_BACK = 7;

/** Preset duration buckets → the bounds the API takes. Upper bounds are
 *  exclusive-by-1ms so neighbouring buckets can't both match one session. */
const DURATION_RANGES: Record<string, { minDurationMs?: number; maxDurationMs?: number }> = {
  'under-1m': { maxDurationMs: 59_999 },
  '1-5m': { minDurationMs: 60_000, maxDurationMs: 299_999 },
  '5-15m': { minDurationMs: 300_000, maxDurationMs: 899_999 },
  '15-60m': { minDurationMs: 900_000, maxDurationMs: 3_599_999 },
  'over-1h': { minDurationMs: 3_600_000 },
};

/** Local calendar day, matching what the date picker emits — `toISOString()`
 *  would shift the default window by a day for anyone east or west of UTC. */
function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoDay(d);
}

export interface UserActionSessionListProps {
  /** Scope to one user and hide the picker. */
  userId?: string;
}

export function UserActionSessionList({ userId: fixedUserId }: UserActionSessionListProps) {
  const [picked, setPicked] = useState<UserSearchOption | null>(null);
  const [pickedEmail, setPickedEmail] = useState('');
  // The cross-user list defaults to the past week — it is a cross-partition
  // fan-out and "what happened lately" is the question it answers. The
  // per-user tab starts open-ended, so one person's whole retained history
  // (30 days by TTL) is there without touching a filter.
  const [applied, setApplied] = useState<Record<string, string>>(() => ({
    appSource: '',
    ring: '',
    startedFrom: fixedUserId ? '' : daysAgo(DEFAULT_DAYS_BACK),
    startedTo: '',
    duration: '',
    search: '',
  }));
  const [sort, setSort] = useState<SortState>({ sortBy: 'startedAt', sortOrder: 'desc' });
  const [page, setPage] = useState(1);

  const userId = fixedUserId ?? picked?.userId ?? '';
  const { appSource, ring, startedFrom, startedTo, duration, search } = applied;

  const sessions = useQuery({
    queryKey: [
      'user-actions', userId, appSource, ring, startedFrom, startedTo, duration, search,
      sort.sortBy, sort.sortOrder, page,
    ],
    queryFn: () => {
      const p = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
      });
      if (userId) p.set('userId', userId);
      if (appSource) p.set('appSource', appSource);
      if (ring) p.set('ring', ring);
      if (search) p.set('search', search);
      // Date inputs give a bare date; widen to the whole day.
      if (startedFrom) p.set('from', `${startedFrom}T00:00:00.000Z`);
      if (startedTo) p.set('to', `${startedTo}T23:59:59.999Z`);
      const range = DURATION_RANGES[duration];
      if (range?.minDurationMs !== undefined) p.set('minDurationMs', String(range.minDurationMs));
      if (range?.maxDurationMs !== undefined) p.set('maxDurationMs', String(range.maxDurationMs));
      return fetchWithAdmin<ListSessionsResponse>(`/console/user-actions?${p}`);
    },
  });

  const filterDefs: FilterDef[] = [
    {
      key: 'appSource',
      placeholder: 'All apps',
      options: [
        { value: 'web-student', label: 'Student web' },
        { value: 'web-teacher', label: 'Teacher web' },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
      ],
    },
    {
      key: 'ring',
      placeholder: 'All rings',
      options: [
        { value: 'prod', label: 'Production' },
        { value: 'beta', label: 'Beta' },
        { value: 'devtesting', label: 'Dev / testing' },
        { value: 'guest', label: 'Guest' },
      ],
    },
    { key: 'started', kind: 'daterange', label: 'Started' },
    {
      key: 'duration',
      placeholder: 'Any duration',
      options: [
        { value: 'under-1m', label: 'Under 1 min' },
        { value: '1-5m', label: '1 – 5 min' },
        { value: '5-15m', label: '5 – 15 min' },
        { value: '15-60m', label: '15 – 60 min' },
        { value: 'over-1h', label: 'Over 1 hour' },
      ],
    },
  ];

  /* Column keys are the API's sort fields — see SESSION_SORT_FIELDS. The user
     column sorts on the denormalized `userEmail` written at session start, and
     displays the live user document. */
  const columns: ColumnDef<SessionSummary>[] = [
    {
      key: 'startedAt',
      header: 'Started',
      sortable: true,
      mobile: 'title',
      render: s => formatStarted(s.startedAt),
    },
    ...(fixedUserId ? [] : [{
      key: 'userEmail',
      header: 'User',
      sortable: true,
      className: 'ua-user',
      render: (s: SessionSummary) => (
        s.user?.name || s.user?.email
          ? (
            <span className="ua-user-stack">
              <span className="ua-user-name">{s.user.name || '—'}</span>
              {s.user.email && <span className="ua-user-email">{s.user.email}</span>}
            </span>
          )
          // Deleted user, or a session recorded before the document existed —
          // the id is all we have.
          : <span className="ua-dim">{s.userId}</span>
      ),
    } as ColumnDef<SessionSummary>]),
    {
      key: 'durationMs',
      header: 'Duration',
      sortable: true,
      render: s => formatDuration(s.durationMs),
    },
    {
      key: 'pageViewCount',
      header: 'Pages',
      sortable: true,
      render: s => <>{s.pageViewCount} <span className="ua-dim">/ {s.eventCount}</span></>,
    },
    {
      key: 'entryPage',
      header: 'Entry → last',
      className: 'ua-route',
      render: s => (
        <>
          <span>{s.entryPage}</span>
          {s.lastPage !== s.entryPage && <span className="ua-arrow"> → {s.lastPage}</span>}
        </>
      ),
    },
    {
      key: 'device',
      header: 'Device',
      className: 'ua-dim',
      render: s => s.device?.deviceClass ?? '—',
    },
    {
      key: 'endedReason',
      header: 'Ended',
      sortable: true,
      className: 'ua-dim',
      render: s => s.endedReason ?? 'active',
    },
  ];

  const data = sessions.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const isEmpty = !!data && data.sessions.length === 0;

  return (
    <div className="ua-list">
      <SearchFilterBar
        filters={filterDefs}
        values={applied}
        onChange={next => { setApplied(next); setPage(1); }}
        searchPlaceholder="Search entry / last page…"
        leading={!fixedUserId && (
          <ScopedUserSearchFilter
            value={pickedEmail}
            onChange={setPickedEmail}
            onPick={opt => { setPicked(opt); setPage(1); }}
            placeholder="Filter by user…"
          />
        )}
      />

      {sessions.isError && <p className="ua-error">Failed: {(sessions.error as Error).message}</p>}

      {isEmpty ? (
        <p className="ua-empty">
          No sessions match these filters. Tracing runs where the{' '}
          <code>user_session_trace</code> gate resolves on, and only in the student and
          teacher apps — the science site records beacons instead, listed separately
          below. Recordings are kept for 30 days.
        </p>
      ) : (
        <>
          {data && <div className="ua-count">{data.total.toLocaleString()} sessions</div>}

          <DataTable
            className="ua-table"
            columns={columns}
            data={data?.sessions ?? []}
            rowKey={s => s.sessionId}
            loading={sessions.isLoading}
            sort={sort}
            onSortChange={next => { setSort(next); setPage(1); }}
            pagination={{
              page,
              totalPages,
              total: data?.total,
              onPageChange: setPage,
            }}
            renderExpandedRow={s => <SessionTimeline userId={s.userId} sessionId={s.sessionId} />}
          />
        </>
      )}
    </div>
  );
}
